import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BalanceContext = createContext();

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (!context) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
};

export const BalanceProvider = ({ children }) => {
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Initialize from localStorage on mount
    return !!localStorage.getItem('token');
  });
  const retryCount = useRef(0);
  const maxRetries = 3;
  const fetchInProgress = useRef(false);

  // Check if user is authenticated
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    const hasToken = !!token;
    setIsAuthenticated(hasToken);
    return hasToken;
  }, []);

  // Fetch balance from backend using axios
  const fetchBalance = useCallback(async (forceRetry = false) => {
    // Prevent concurrent fetches
    if (fetchInProgress.current && !forceRetry) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // User not logged in - clear data silently
        setBalanceData(null);
        setError(null);
        setLoading(false);
        setIsAuthenticated(false);
        return;
      }

      fetchInProgress.current = true;
      setIsAuthenticated(true);
      setLoading(true);
      setError(null);

      const BACKEND_URL = '';
      
      const response = await axios.get(`${BACKEND_URL}/api/wallet/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 20000 // 20 second timeout
      });

      if (response.data && response.data.balances) {
        setBalanceData(response.data);
        setError(null);
        retryCount.current = 0;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Token invalid - clear it
        setError('Session expired. Please login again.');
        setIsAuthenticated(false);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Network Error')) {
        // Network/timeout error - retry
        if (retryCount.current < maxRetries) {
          retryCount.current += 1;
          console.log(`Balance fetch failed, retrying (${retryCount.current}/${maxRetries})...`);
          fetchInProgress.current = false;
          setTimeout(() => fetchBalance(true), 2000 * retryCount.current);
          return;
        }
        setError('Connection issue. Please check your internet and refresh.');
      } else {
        const errorMessage = err.response?.data?.detail || 'Unable to load balance. Please refresh the page.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, []);

  // Initial fetch on mount - with multiple attempts for timing issues
  useEffect(() => {
    let mounted = true;
    let attempts = 0;
    const maxAttempts = 5;

    const attemptFetch = () => {
      if (!mounted) return;
      
      const token = localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
        fetchBalance();
      } else if (attempts < maxAttempts) {
        attempts++;
        // Retry with exponential backoff
        setTimeout(attemptFetch, 100 * attempts);
      } else {
        setLoading(false);
        setIsAuthenticated(false);
      }
    };

    attemptFetch();

    return () => {
      mounted = false;
    };
  }, [fetchBalance]);

  // Listen for storage changes (token added/removed)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          setIsAuthenticated(true);
          fetchBalance();
        } else {
          setBalanceData(null);
          setError(null);
          setIsAuthenticated(false);
        }
      }
    };

    const handleTokenChange = () => {
      const token = localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
        fetchBalance();
      } else {
        setBalanceData(null);
        setError(null);
        setIsAuthenticated(false);
      }
    };

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    // Custom event for same-tab token changes
    window.addEventListener('tokenChanged', handleTokenChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenChanged', handleTokenChange);
    };
  }, [fetchBalance]);

  // Auto-refresh every 60 seconds if authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        fetchBalance();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchBalance, isAuthenticated]);

  // Helper functions
  const getCurrencyBalance = useCallback((currency) => {
    return balanceData?.balances?.[currency] || 0;
  }, [balanceData]);

  const getTotalBalanceUSDT = useCallback(() => {
    return balanceData?.total_balance_usdt || 0;
  }, [balanceData]);

  const getHoldings = useCallback(() => {
    return balanceData?.holdings || [];
  }, [balanceData]);

  const isUsingLivePrices = useCallback(() => {
    return balanceData?.using_live_prices || false;
  }, [balanceData]);

  const value = {
    balanceData,
    loading,
    error,
    isAuthenticated,
    refreshBalance: fetchBalance,
    getCurrencyBalance,
    getTotalBalanceUSDT,
    getHoldings,
    isUsingLivePrices,
    balances: balanceData?.balances || {},
    totalBalanceUSDT: balanceData?.total_balance_usdt || 0,
    holdings: balanceData?.holdings || [],
    formattedTotal: balanceData?.formatted_total || '$0.00',
    lastUpdated: balanceData?.last_updated,
    hasError: !!error && isAuthenticated
  };

  return (
    <BalanceContext.Provider value={value}>
      {children}
    </BalanceContext.Provider>
  );
};
